import {
  inject,
  Component,
  Input,
  Output,
  EventEmitter,
  signal,
  computed,
  ChangeDetectionStrategy,
} from '@angular/core';

import { Store } from '@ngrx/store';
import { toSignal } from '@angular/core/rxjs-interop';

import {
  switchUserCompany,
  switchUserCompanySuccess,
} from 'src/app/core/actions/auth.actions';
import {
  injectActivatedUserCompaniesQuery,
  injectSwitchCompanyMutation,
} from 'src/app/api/queries/user.queries';
import { AppState } from '../../../reducers';

import { BehaviorSubject, debounceTime, distinctUntilChanged } from 'rxjs';

@Component({
  selector: 'app-company-switcher',
  templateUrl: './company-switcher.component.html',
  styleUrls: ['./company-switcher.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CompanySwitcherComponent {
  @Input() activeCompany: string;
  @Input() accessLevel: string;

  /** Emitted when a company is picked, so a host popover/dropdown can close. */
  @Output() companySelected = new EventEmitter<void>();

  private readonly store = inject(Store<AppState>);

  private readonly filterSubject = new BehaviorSubject<string>('');
  private readonly searchTerm = toSignal(
    this.filterSubject.pipe(debounceTime(300), distinctUntilChanged()),
    { initialValue: '' },
  );
  private readonly showAll = signal(false);

  private readonly companiesQuery = injectActivatedUserCompaniesQuery(
    this.searchTerm,
    this.showAll,
  );
  private readonly switchCompanyMutation = injectSwitchCompanyMutation();

  readonly companies = computed(
    () => this.companiesQuery.data()?.companies ?? [],
  );
  readonly loading = computed(() => this.companiesQuery.isFetching());
  readonly moreCount = computed(
    () => (this.companiesQuery.data()?.total ?? 0) - this.companies().length,
  );

  loadMore() {
    this.showAll.set(true);
  }

  onFilterChange(filter: string) {
    // A new search always returns to the first-page view.
    this.showAll.set(false);
    this.filterSubject.next(filter);
  }

  selectCompany(company: any) {
    // A pure broadcast, but four listeners hang off it: the query cache clear,
    // `NomenclatureService`, `OutputSettingsService` and `core.reducer`'s error
    // reset. Dropping this dispatch would take all four with it, silently.
    this.store.dispatch(switchUserCompany({ companyId: company.id }));

    // `mutateAsync`, not a per-call `onSuccess`: the emit below closes the host
    // popover and destroys us, and only the promise survives that teardown.
    this.switchCompanyMutation
      .mutateAsync(company.id)
      .then((user) => this.store.dispatch(switchUserCompanySuccess({ user })))
      .catch(() => {});

    this.companySelected.emit();
  }
}
